import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {AuthService} from '../../../Services/auth.service';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {EncrDecrService} from '../../../Services/encr-decr.service';
import {SysUserModel} from '../../../DataModels/sys-user-model';
import {EventService} from '../../../Services/event-service';
import {PreferencesService} from '../../../Services/preferences.service';
import {AppConfigService} from '../../../Services/app-config.service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {lastValueFrom} from 'rxjs';
import {EmailService} from '../../../Services/email.service';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css'],
  standalone: false
})
export class UserProfileComponent implements OnInit {
  profileForm: UntypedFormGroup;
  userInfo: SysUserModel;
  isLoaded = false;
  isDragging = false;
  companies: any[] = [];
  avatarUrl: string = '';
  calendarToken: string = '';
  calendarUrl: string = '';

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private dataService: DataService,
    private storage: StorageService,
    private notification: BsToastService,
    private crypt: EncrDecrService,
    private eventService: EventService,
    private preferencesService: PreferencesService,
    private appConfigService: AppConfigService,
    private confirmDialog: ConfirmDialogService,
    private emailService: EmailService
  ) {
  }

  ngOnInit(): void {
    this.createForm();
    this.loadData();
    this.loadCalendarToken();
  }

  createForm() {
    this.profileForm = this.fb.group({
      name: new UntypedFormControl({value: '', disabled: true}),
      email: new UntypedFormControl({value: '', disabled: true}),
      defaultCompany: new UntypedFormControl(''),
      showBatches: new UntypedFormControl(false),
      avatarUrl: new UntypedFormControl('')
    });
  }

  loadData() {
    this.isLoaded = false;
    // Get current user info from auth service
    this.userInfo = this.authService.currentUser();

    // Fetch fresh user info from API to get latest preferences
    this.dataService.getData('sys_user/me').subscribe({
      next: (user: SysUserModel) => {
        // Merge fresh data with existing user info to preserve fields not returned by /me endpoint
        this.userInfo = {...this.userInfo, ...user};
        const prefs = JSON.parse(this.userInfo.Preferences || '{}');

        this.profileForm.patchValue({
          name: user.name,
          email: user.email,
          defaultCompany: prefs.default_company_id || '',
          showBatches: prefs.ShowBatches || false,
          avatarUrl: prefs.avatarUrl || ''
        });

        this.avatarUrl = this.buildAvatarDisplayUrl(prefs.avatarUrl);

        // Load companies for the dropdown
        this.loadCompanies();
      },
      error: (err) => {
        this.notification.showErrorToast('Error loading user profile');
        this.isLoaded = true;
      }
    });
  }

  loadCompanies() {
    this.dataService.getApiListData('company', '?pageNumber=0&pageSize=999999').subscribe({
      next: (res: any) => {
        if (res && Array.isArray(res.Data)) {
          this.companies = res.Data;
        } else if (Array.isArray(res)) {
          this.companies = res;
        } else if (res && Array.isArray(res.data)) {
          this.companies = res.data;
        } else {
          this.companies = [];
        }
        this.isLoaded = true;
      },
      error: (err) => {
        console.error('Error loading companies', err);
        this.companies = [];
        this.isLoaded = true;
      }
    });
  }

  async loadCalendarToken() {
    try {
      const token = await lastValueFrom(this.dataService.getData('calendar/token'));
      this.calendarToken = token as string;
      this.calendarUrl = `${this.appConfigService.apiBaseUrl}api/calendar/subscribe/${this.calendarToken}`;
    } catch (err) {
      this.notification.showErrorToast('Error loading calendar token');
    }
  }

  copyCalendarUrl() {
    navigator.clipboard.writeText(this.calendarUrl).then(() => {
      this.notification.showSuccessToast('Calendar URL copied to clipboard.');
    });
  }

  emailCalendarLink() {
    const email = this.userInfo?.email || '';
    this.emailService.sendEmail({
      toEmail: email,
      subject: 'Your Calendar Subscription Link',
      body: `Your calendar subscription URL:\n\n${this.calendarUrl}\n\nAdd this URL in your calendar app (iPhone Settings > Calendar > Accounts > Add Account > Other > Add Subscribed Calendar).`,
      isBodyHtml: false
    });
    this.notification.showSuccessToast('Calendar link emailed to ' + email);
  }

  resetCalendarToken() {
    this.confirmDialog.confirmThis(
      [
        'Reset Calendar Link?',
        'This will generate a new subscription URL.',
        'danger: All external apps (iPhone, Google Calendar, etc.) using the current link will stop working and must be reconfigured with the new URL.'
      ],
      async () => {
        try {
          const token = await lastValueFrom(this.dataService.post('calendar/token/reset', {}));
          this.calendarToken = (token as any)?.token || token as string;
          this.calendarUrl = `${this.appConfigService.apiBaseUrl}api/calendar/subscribe/${this.calendarToken}`;
          this.notification.showSuccessToast('Calendar link reset successfully.');
        } catch (err) {
          this.notification.showErrorToast('Error resetting calendar token');
        }
      },
      () => {
      }
    );
  }

  uploadAvatar(file: File) {
    if (!file.type.startsWith('image/')) {
      this.notification.showErrorToast('Please select an image file');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    // Do NOT set Content-Type — let the browser set the multipart boundary
    this.dataService.postWithPromise(`Profile/${this.userInfo.id}/avatar`, formData).then(
      (res: any) => {
        if (res && res.avatarUrl) {
          this.avatarUrl = this.buildAvatarDisplayUrl(res.avatarUrl);
          this.profileForm.patchValue({avatarUrl: res.avatarUrl});
          this.notification.showSuccessToast('Avatar updated successfully');
          this.eventService.broadcast('PreferencesUpdated', null);
        }
      },
      () => {
        this.notification.showErrorToast('Error uploading avatar');
      }
    );
  }

  private buildAvatarDisplayUrl(relativePath?: string): string {
    if (!relativePath) {
      return 'assets/images/users/avatar-1.jpg';
    }
    const prefix = (this.appConfigService.avatarsStorageUrlPrefix || '').replace(/\/$/, '');
    const cleanPath = relativePath.replace(/^\/uploads/, '');
    const path = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    return `${prefix}${path}`;
  }

  onFileSelected(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.uploadAvatar(files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.uploadAvatar(files[0]);
    }
  }

  onSubmit() {
    if (this.profileForm.valid) {
      const formValues = this.profileForm.getRawValue();

      const currentPrefs = JSON.parse(this.userInfo.Preferences || '{}');

      const updatedPrefs = {
        ...currentPrefs,
        default_company_id: formValues.defaultCompany,
        ShowBatches: formValues.showBatches,
        avatarUrl: this.avatarUrl || currentPrefs.avatarUrl || ''
      };

      // API now accepts a plain object — no double-encoding needed
      this.dataService.patch('Profile', `${this.userInfo.id}/preferences`, updatedPrefs).then(
        (res) => {
          this.notification.showSuccessToast('Profile updated successfully');

          // Update local preferences storage and object
          const prefsJson = JSON.stringify(updatedPrefs);
          this.storage.StorageSet('Preferences', this.crypt.encrypt(prefsJson));
          this.userInfo.Preferences = prefsJson;

          // Update PreferencesService in-memory map
          this.preferencesService.LoadPreferences(updatedPrefs);

          // Update currentUser in localStorage for persistence across reloads
          const storedUserRaw = localStorage.getItem('currentUser');
          if (storedUserRaw) {
            const storedUser = JSON.parse(storedUserRaw);
            const updatedUser = {...storedUser, Preferences: prefsJson};
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          }

          // Notify other components
          this.eventService.broadcast('PreferencesUpdated', null);
        },
        (err) => {
          this.notification.showErrorToast('Error updating profile');
        }
      );
    }
  }
}
